import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LazyLoadEvent, TreeNode } from 'primeng/primeng';
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { ResponsePageable } from '../../shared/_utils/services/responsePageable';
import { QueryOperator } from '../../shared/_utils/services/query-operator.enum';
import { UbigeoType } from '../../shared/_utils/services/ubigeoType.enum';
import { Country } from '../entities/country';
import { Department } from '../entities/department';
import { District } from '../entities/district';
import { Province } from '../entities/province';
import { Ubigeo } from '../entities/ubigeo';
import { CountryService } from './country.service';
import { DepartmentService } from './department.service';
import { DistrictService } from './district.service';
import { ProvinceService } from './province.service';
import { SpinnerService } from './spinner.service';

@Injectable()
export class UbigeoService {

    protected rutaServidor = localStorage.getItem('rutaServidor');
    protected httpParams = new HttpParams();
    protected uri = '/api/ubigeo/';

    constructor(
        private httpClient: HttpClient,
        private countryservice: CountryService,
        private departmentService: DepartmentService,
        private provinceService: ProvinceService,
        private districtService: DistrictService,
        private spinnerService: SpinnerService
    ) {
       //console.log('Instance UbigeoService');
    }

    loadCountries(): Observable<ResponsePageable<Country>> {
        return this.countryservice.search();
    }

    loadDepartments(ubigeo: Ubigeo): Observable<ResponsePageable<Department>> {
        const department = new Department();
        department.country = ubigeo.country;
        return this.departmentService.search(department);
    }

    loadProvinces(ubigeo: Ubigeo): Observable<ResponsePageable<Province>> {
        const province = new Province();
        province.department = ubigeo.department;
        return this.provinceService.search(province);
    }

    loadDistricts(ubigeo: Ubigeo): Observable<ResponsePageable<District>> {
        const district = new District();
        district.province = ubigeo.province;
        return this.districtService.search(district);
    }

    buildSentence(entity?: Ubigeo, event?: LazyLoadEvent): HttpParams {
        if (event) {
            const page = ((event.first + event.rows) / event.rows - 1).toString();
            const size = event.rows.toString();
            this.httpParams = this.httpParams.set(QueryOperator.PageParam, page);
            this.httpParams = this.httpParams.set(QueryOperator.SizeParam, size);
        }
        return this.httpParams;
    }

    search(ubigeo: Ubigeo, event?: LazyLoadEvent): Observable<ResponsePageable<Ubigeo>> {
        let url = this.rutaServidor + this.uri;
        url = ubigeo.country ? url + ubigeo.country.code + QueryOperator.Slash : url;
        url = ubigeo.department ? url + ubigeo.department.code + QueryOperator.Slash : url;
        url = ubigeo.province ? url + ubigeo.province.code + QueryOperator.Slash : url;
        url = ubigeo.district ? url + ubigeo.district.code + QueryOperator.Slash : url;

        // debugger;
        this.spinnerService.showAll();
        return this.httpClient
            .get<ResponsePageable<Ubigeo>>(url, { params: this.buildSentence() })
            .pipe(
                tap(response => {
                   //console.log(response);
                    this.spinnerService.hideAll();
                    return response;
                })
            );
    }

    toTreeNode(ubigeos: Ubigeo[]): TreeNode[] {
        return this.bindCountries(ubigeos);
    }

    private bindCountries(ubigeos: Ubigeo[]): TreeNode[] {
        const treeNodes: TreeNode[] = [];
        let auxCountryId = '';
        ubigeos.forEach((ubigeo) => {
            if (auxCountryId !== ubigeo.country.id) {
                const treeNode = {
                    data: {value: ubigeo.country, type: UbigeoType.Country},
                    expanded: true,
                    children: this.bindDepartments(ubigeos, ubigeo.country)
                }
                treeNodes.push(treeNode);
                auxCountryId = ubigeo.country.id;
            }
        })
        return treeNodes;
    }

    private bindDepartments(ubigeos: Ubigeo[], country: Country): TreeNode[] {
        const treeNodes: TreeNode[] = [];
        let auxDepartmentId = '';
        ubigeos.forEach((ubigeo) => {
            if (
                ubigeo.department
                && ubigeo.country
                && country
                && ubigeo.country.id === country.id
                && auxDepartmentId !== ubigeo.department.id
            ) {
                ubigeo.department.country = ubigeo.country;
                const treeNode = {
                    data: {value: ubigeo.department, type: UbigeoType.Department},
                    children: this.bindProvinces(ubigeos, ubigeo.department)
                }
                treeNodes.push(treeNode);
                auxDepartmentId = ubigeo.department.id;
            }
        })
        return treeNodes;
    }

    private bindProvinces(ubigeos: Ubigeo[], department: Department): TreeNode[] {
        const treeNodes: TreeNode[] = [];
        let auxProvinceId = '';
        ubigeos.forEach((ubigeo: Ubigeo) => {
            if (
                ubigeo.province
                && ubigeo.department
                && department
                && ubigeo.department.id === department.id
                && auxProvinceId !== ubigeo.province.id
            ) {
                ubigeo.province.department = ubigeo.department;
                const treeNode = {
                    data: {value: ubigeo.province, type: UbigeoType.Province},
                    children: this.bindDistricts(ubigeos, ubigeo.province)
                }
                treeNodes.push(treeNode);
                auxProvinceId = ubigeo.province.id;
            }
        })
        return treeNodes;
    }

    private bindDistricts(ubigeos: Ubigeo[], province: Province): TreeNode[] {
        const treeNodes: TreeNode[] = [];
        ubigeos.forEach((ubigeo: Ubigeo) => {
            if (
                ubigeo.district
                && ubigeo.province
                && province
                && ubigeo.province.id === province.id
            ) {
                ubigeo.district.province = ubigeo.province;
                let treeNode: TreeNode;
                treeNode = {
                    data: {value: ubigeo.district, type: UbigeoType.District}
                }
                treeNodes.push(treeNode);
            }
        })
        return treeNodes;
    }

    insert(newUbigeo: any, selectedUbigeo: TreeNode): Observable<any> {
        // debugger;
        const type = selectedUbigeo.data.type;
        if (this.isCountry(type)) {
            return this.countryservice.insert(newUbigeo);
        } else if (this.isDepartment(type)) {
            const department = new Department();
            department.code = newUbigeo.code;
            department.name = newUbigeo.name;
            department.country = <Country>selectedUbigeo.parent.data.value;
            return this.departmentService.insert(department);
        } else if (this.isProvince(type)) {
            const province = new Province();
            province.code = newUbigeo.code;
            province.name = newUbigeo.name;
            province.department = <Department>selectedUbigeo.parent.data.value;
            return this.provinceService.insert(province);
        } else if (this.isDistrict(type)) {
            const district = new District();
            district.code = newUbigeo.code;
            district.name = newUbigeo.name;
            district.province = <Province>selectedUbigeo.parent.data.value;
            return this.districtService.insert(district);
        }
    }

    update(newUbigeo: any, selectedUbigeo: TreeNode): Observable<any> {
        // debugger;
        const type = selectedUbigeo.data.type;
        const id = selectedUbigeo.data.value.id;

        if (this.isCountry(type)) {
            return this.countryservice.update(id, newUbigeo);
        } else if (this.isDepartment(type)) {
            const department = new Department();
            department.id = id;
            department.code = newUbigeo.code;
            department.name = newUbigeo.name;
            department.country = <Country>selectedUbigeo.parent.data.value;
            return this.departmentService.update(id, department);
        } else if (this.isProvince(type)) {
            const province = new Province();
            province.id = id;
            province.code = newUbigeo.code;
            province.name = newUbigeo.name;
            province.department = <Department>selectedUbigeo.parent.data.value;
            return this.provinceService.update(id, province);
        } else if (this.isDistrict(type)) {
            const district = new District();
            district.id = id;
            district.code = newUbigeo.code;
            district.name = newUbigeo.name;
            district.province = <Province>selectedUbigeo.parent.data.value;
            return this.districtService.update(id, district);
        }
    }

    delete(selectedUbigeo: TreeNode): Observable<any> {
        // debugger;
        const id = selectedUbigeo.data.value.id;
        if (this.isCountry(selectedUbigeo.data.type)) {
            return this.countryservice.delete(id);
        } else if (this.isDepartment(selectedUbigeo.data.type)) {
            return this.departmentService.delete(id);
        } else if (this.isProvince(selectedUbigeo.data.type)) {
            return this.provinceService.delete(id);
        } else if (this.isDistrict(selectedUbigeo.data.type)) {
            return this.districtService.delete(id);
        }
    }

    getOne(selectedUbigeo: TreeNode): Observable<any> {
        // debugger;
        const type = selectedUbigeo.data.type;
        const id = selectedUbigeo.data.value.id;
        if (this.isCountry(type)) {
            return this.countryservice.getOneById(id);
        } else if (this.isDepartment(type)) {
            return this.departmentService.getOneById(id);
        } else if (this.isProvince(type)) {
            return this.provinceService.getOneById(id);
        } else if (this.isDistrict(type)) {
            return this.districtService.getOneById(id);
        }
    }

    getOneByCode(selectedUbigeo: TreeNode): Observable<any> {
        // debugger;
        const type = selectedUbigeo.data.type;
        const code = selectedUbigeo.data.value.code;
        if (this.isCountry(type)) {
            return this.countryservice.getOneByCode(code);
        } else if (this.isDepartment(type)) {
            return this.departmentService.getOneByCode(code);
        } else if (this.isProvince(type)) {
            return this.provinceService.getOneByCode(code);
        } else if (this.isDistrict(type)) {
            return this.districtService.getOneByCode(code);
        }
    }

    isCountry(type: string) {
        return UbigeoType.Country === type;
    }

    isDepartment(type: string) {
        return UbigeoType.Department === type;
    }

    isProvince(type: string) {
        return UbigeoType.Province === type;
    }

    isDistrict(type: string) {
        return UbigeoType.District === type;
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/ubigeo.service.ts